import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";

export default function Privacy() {
  return (
    <div data-testid="privacy-page">
      <Seo
        title="Privacy policy"
        description="Privacy policy for TerminalValue.io — what we collect, why, and how to contact us."
        path="/privacy"
      />
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-8 text-[14px] leading-relaxed text-[#222]">
        <h1 className="text-xl font-bold border-b border-black pb-2">{">"}_ privacy policy</h1>

        <p className="text-[12px] text-[#888] mt-3">Last updated: June 2026</p>

        <Section title="1. Information we collect">
          <Sub title="Upvotes">
            When you upvote a tool, we store your IP address and the associated tool ID to
            prevent duplicate voting and abuse. No accounts or cookies are required for this
            feature.
          </Sub>
          <Sub title="Submissions">
            When you submit a tool, we collect the information you provide, such as title, URL,
            description, category, features, and pricing. You may optionally provide an email
            address if you would like to be contacted.
          </Sub>
          <Sub title="Contact messages">
            When you contact us, we store the information you submit (such as name, email,
            subject, and message content), along with your IP address for security and spam
            prevention.
          </Sub>
          <Sub title="AI Concierge">
            When you ask the AI Concierge a question, we store the text of your
            question (and the assistant&apos;s reply) so the conversation can
            continue across follow-ups. We also keep aggregate analytics — the
            questions asked, how often the concierge is used, and which part of
            the site it was used from — to improve the tool and our content. The
            analytics records do <span className="font-bold">not</span> include
            your IP address and are automatically deleted after about 180 days.
            Please avoid entering personal or sensitive information into the
            concierge.
          </Sub>
          <Sub title="Usage &amp; engagement analytics">
            We keep first-party, anonymous analytics about how the site is used —
            for example pages viewed, searches run, tools upvoted, terminals
            created, and which third-party tools are clicked out to and from
            which section of the site (home, tool page, concierge, or terminal).
            These records use a random, browser-generated visitor id (not tied to
            your name or account) and do <span className="font-bold">not</span>{" "}
            store your IP address (we keep only a short-lived, salted hash used to
            filter bots). This data is first-party — it is not sold or shared with
            advertisers — and is used only to understand engagement and improve
            the index. We may also use privacy-respecting third-party analytics
            (see Cookies and analytics below).
          </Sub>
          <Sub title="Outbound clicks">
            When you click a link to a third-party tool, the click is routed
            through our own redirect so we can record (anonymously, as above)
            which tool was clicked and which section it was clicked from. We do
            not pass your identity to the destination; the third party may still
            receive standard technical information (see Third-party services).
          </Sub>
          <Sub title="Server logs">
            Our infrastructure automatically records standard server logs (including IP address,
            browser type, requested pages, and timestamps) for security, reliability, and system
            maintenance. These logs are typically retained for up to 30 days.
          </Sub>
        </Section>

        <Section title="2. Cookies and analytics">
          <p>
            We use cookies and similar technologies to operate the site and measure usage.
          </p>
          <p className="mt-2">
            We use Google Analytics to understand how visitors interact with the site. This helps
            us improve performance, content organization, and user experience.
          </p>
          <p className="mt-2">Google Analytics may collect information such as:</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>Pages visited and time spent on the site</li>
            <li>Device and browser information</li>
            <li>Approximate location (based on IP address)</li>
            <li>Technical identifiers stored in cookies</li>
          </ul>
          <p className="mt-2">
            This data is used in aggregate form and is not used to personally identify individual
            users.
          </p>
          <p className="mt-2">
            You can control or disable cookies through your browser settings. Disabling cookies
            may affect site functionality.
          </p>
          <p className="mt-2">We use Google Analytics for usage measurement.</p>
        </Section>

        <Section title="3. How we use information">
          <p>We use collected information to:</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>Operate and maintain the site</li>
            <li>Prevent spam, abuse, and manipulation (including vote fraud)</li>
            <li>Improve content, structure, and discovery</li>
            <li>Respond to user inquiries</li>
            <li>Maintain security and system reliability</li>
          </ul>
        </Section>

        <Section title="4. Third-party services">
          <p>
            The site links to third-party tools and websites. When you click an external link,
            those third parties may receive standard technical information such as your IP
            address and referring page, and their own privacy policies apply.
          </p>
          <p className="mt-2">
            We may also use third-party service providers (such as hosting providers, analytics
            providers, or infrastructure tools) that process limited technical data as part of
            delivering the site.
          </p>
        </Section>

        <Section title="5. Data retention">
          <p>We retain data only as long as necessary for operational and security purposes:</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>Upvote data: retained as long as needed to prevent abuse and maintain integrity</li>
            <li>Submissions: retained as part of the public tool index history</li>
            <li>Contact messages: typically retained for 90–365 days</li>
            <li>Concierge conversations &amp; question analytics: analytics records auto-delete after ~180 days</li>
            <li>Server logs: retained for up to 30 days</li>
            <li>Analytics data: retained according to Google Analytics settings</li>
          </ul>
        </Section>

        <Section title="6. Legal basis for processing (EEA / UK users)">
          <p>
            If you are located in the European Economic Area (EEA) or United Kingdom, we process
            your personal data under the following legal bases:
          </p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>
              <span className="font-bold">Legitimate interests:</span> to operate, secure, and
              improve the site (including abuse prevention, logging, and upvote integrity)
            </li>
            <li>
              <span className="font-bold">Consent:</span> where you voluntarily provide
              information, such as submitting a tool or providing an email address
            </li>
            <li>
              <span className="font-bold">Legal obligation:</span> where we are required to
              retain or disclose information under applicable law
            </li>
          </ul>
          <p className="mt-2">We only process data that is necessary for these purposes.</p>
        </Section>

        <Section title="7. Your rights (EEA / UK users)">
          <p>If you are located in the EEA or UK, you have the right to:</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>Access the personal data we hold about you</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your personal data</li>
            <li>Object to or restrict certain processing activities</li>
            <li>Request portability of your data (where applicable)</li>
          </ul>
          <p className="mt-2">
            To exercise these rights,{" "}
            <Link to="/contact" className="tv-link">contact us</Link>.
          </p>
          <p className="mt-2">
            We will respond within the timeframes required by applicable law.
          </p>
        </Section>

        <Section title="8. International data transfers">
          <p>
            Your information may be processed in countries outside your own, including the United
            States, where data protection laws may differ.
          </p>
          <p className="mt-2">
            Where required, we rely on appropriate safeguards for international data transfers.
          </p>
        </Section>

        <Section title="9. Security">
          We implement reasonable technical and organizational measures to protect your data.
          However, no system is completely secure, and we cannot guarantee absolute security of
          information.
        </Section>

        <Section title="10. Children's privacy">
          This site is not directed to children under 13 (or under 16 in the EEA/UK), and we do
          not knowingly collect personal data from children.
        </Section>

        <Section title="11. Changes to this policy">
          We may update this Privacy Policy from time to time. The &ldquo;Last updated&rdquo; date
          reflects the most recent version. Continued use of the site after changes indicates
          acceptance of the updated policy.
        </Section>

        <Section title="12. Contact">
          Privacy questions or data-subject requests?{" "}
          <Link to="/contact" className="tv-link">Contact us</Link>.
        </Section>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-5">
      <h2 className="text-[14px] font-bold">{title}</h2>
      <div className="text-[13px] mt-1 text-[#222]">{children}</div>
    </section>
  );
}

function Sub({ title, children }) {
  return (
    <div className="mt-2">
      <div className="text-[13px] font-bold">{title}</div>
      <div className="text-[13px] mt-0.5">{children}</div>
    </div>
  );
}
